import { CSSProperties, ReactNode } from 'react';

import { CFPBox, CFPButton } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { AppComposableTypography, InvalidDataAlert } from '@components';
import { UIHelper } from '@helpers';

import { FormHint } from '../formHint';
import { MultichannelInput } from '../multichannelInput';
import { resources, useMultichannelFormHelper } from './multichannelForm.helper';

type MultichannelFormProps = {
	logo?: ReactNode;
	styles?: CSSProperties;
};

export const MultichannelForm = ({ logo, styles }: MultichannelFormProps) => {
	const {
		isFormValid,
		multichannelValidations,
		confirmMultichannelRef,
		accountUserCredentialsErrorMessage,
		positionsToAsk,
		t,
		handleMultichannelChange,
		handleMultichannelComplete,
		handleConfirmMultichannelChange,
		getCFPInputProps,
		submitForm,
	} = useMultichannelFormHelper();

	return (
		<CFPBox stack styles={styles}>
			{logo && <CFPBox styles={{ marginBottom: UIHelper.rem(48) }}>{logo}</CFPBox>}
			<AppComposableTypography appearance="subtitle-200-01" text={t(resources.title)} />

			<CFPBox stack styles={{ marginTop: UIHelper.rem(40), gap: UIHelper.rem(8) }}>
				<AppComposableTypography appearance="body-100-03" text={t(resources.input0.label)} />
				<MultichannelInput
					{...getCFPInputProps('oldMultichannel')}
					autoFocus
					length={positionsToAsk?.length}
					renderLabel={(index) => `${positionsToAsk?.[index]}ª`}
				/>
			</CFPBox>

			<CFPBox stack styles={{ marginTop: UIHelper.rem(32), gap: UIHelper.rem(8) }}>
				<AppComposableTypography appearance="body-100-03" text={t(resources.input1.label)} />
				<MultichannelInput
					{...getCFPInputProps('multichannel')}
					onChange={handleMultichannelChange}
					onComplete={handleMultichannelComplete}
				/>
			</CFPBox>

			<CFPBox stack styles={{ marginTop: UIHelper.rem(16), gap: UIHelper.rem(8) }}>
				<FormHint
					label={t(resources.hint.noSequentialNumbers)}
					isValid={
						!multichannelValidations.required?.message &&
						!multichannelValidations.noSequentialNumbers?.message
					}
				/>
				<FormHint
					label={t(resources.hint.noRepeatedNumbers)}
					isValid={
						!multichannelValidations.required?.message &&
						!multichannelValidations.noRepeatedNumbers?.message
					}
				/>
				<FormHint
					label={t(resources.hint.requiredLength)}
					isValid={
						!multichannelValidations.required?.message &&
						!multichannelValidations.allowedChars?.message &&
						!multichannelValidations.requiredLength?.message
					}
				/>
			</CFPBox>

			<CFPBox stack styles={{ marginTop: UIHelper.rem(32), gap: UIHelper.rem(8) }}>
				<AppComposableTypography appearance="body-100-03" text={t(resources.input2.label)} />
				<MultichannelInput
					{...getCFPInputProps('confirmMultichannel')}
					ref={confirmMultichannelRef}
					editable={multichannelValidations.isValid}
					onChange={handleConfirmMultichannelChange}
				/>
			</CFPBox>

			<CFPBox stack styles={{ alignSelf: 'start', marginTop: UIHelper.rem(24), gap: UIHelper.rem(40) }}>
				<InvalidDataAlert description={accountUserCredentialsErrorMessage || ''} />

				<CFPButton
					label={t(resources.button.continue)}
					size="large"
					endContent={ArrowRight.Outline}
					disabled={!isFormValid}
					onPress={submitForm}
				/>
			</CFPBox>
		</CFPBox>
	);
};
